// lib/slides/reviewStatus.ts
import type { SlideDeckDTO } from '#types/types';
import { SLIDES_VALIDATION_POINTS, VERDICT_MARK } from './validationPoints';

/** The directive behind Launch review and Review, sent as a hidden prompt. */
export const REVIEW_PROMPT =
  'Run a full review of this deck against the active guidelines. Use the project context already supplied in this conversation; start now without asking for more context. ' +
  'Start with Pass 0 - inventory and label every page - then work the guideline points p1..p10 in order, ' +
  'placing localized annotations as you go, and close with the stamp and synthesis.';

// The first step of a review on a deck with no brief yet: the review itself starts once the brief is
// saved (startReviewAfterResearch, at the end of the turn or script chain).
export const BRAND_RESEARCH_PROMPT =
  'Research the brand in this deck: the review starts automatically once the brief is saved. Identify it from the deck and conversation, asking only if unclear. ' +
  'Use SLIDES_IDENTIFY_BRAND, then SLIDES_WEB_SEARCH (at most three searches) for the latest documented retail concept and visual references. ' +
  'Finish web research BEFORE SLIDES_READ_BRAND_CONTEXT, then SLIDES_COLLECT_BRAND_REFERENCES and SLIDES_SAVE_BRAND_RESEARCH. ' +
  'Save a concise cited brief with dates and market relevance, distinguishing evidence and uncertainty. Guidelines always outrank brand inspiration. ' +
  'Do not launch the review yourself or alter the slides.';

// What a person types to press Launch review / Continue review, in English or French. Only the whole
// message counts: "start the review" is the button, "can you review page 3?" is a question for the agent.
const POLITE = /\b(please|pls|plz|stp|svp|s il te plait|s il vous plait|can you|could you|would you|peux tu|pouvez vous|tu peux|vous pouvez|merci|thanks|now|maintenant|go ahead|ok|okay|let s|on|vas y|allez)\b/g;
const OBJECT = '(?:(?:the|a|la|une|l|ma|my) )?(?:full |complete |entire )?(?:review|revue|analyse|analysis|relecture|validation)(?: complete| du deck| of the deck| of this deck| de ce deck)?';
const START = new RegExp(`^(?:start|launch|run|begin|do|lance|lancer|lancez|lances|demarre|demarrer|demarrez|commence|commencer|commencez|fais|faire|faites|execute)(?: ${OBJECT})?$`);
const CONTINUE = new RegExp(`^(?:continue|resume|carry on|keep going|reprends|reprendre|reprenez|poursuis|poursuivre|poursuivez|continuer|continuez|continues)(?: ${OBJECT})?$`);

/** 'start' or 'continue' when the whole message asks to launch the review, else null. */
export function typedReviewIntent(message: string): 'start' | 'continue' | null {
  const words = message.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[’']/g, ' ').replace(/[^a-z0-9 ]+/g, ' ').replace(POLITE, ' ').replace(/\s+/g, ' ').trim();
  if (!words || words.split(' ').length > 6) return null;
  // A bare verb ("start", "lance") is too vague to launch on; with the review named, or "continue" alone, it is the button.
  if (START.test(words) && words.split(' ').length > 1) return 'start';
  return CONTINUE.test(words) ? 'continue' : null;
}

/** The RDM team's filing name, "AAMMDD_CENTRE_PRENEUR_CS REVIEW" (Louis Viossat, 2026-10-10), from the
 *  stamp's centre and tenant; null while either is unknown, so the caller keeps the deck's own name. */
export function reviewExportBaseName(deck: Pick<SlideDeckDTO, 'doc'>, today = new Date()): string | null {
  const header = deck.doc.review?.header;
  const part = (value?: string) => value?.replace(/["*/:<>?\\|_]/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase();
  const centre = part(header?.centre), tenant = part(header?.cellule ?? deck.doc.brandResearch?.brand.name);
  if (!centre || !tenant) return null;
  const date = [today.getFullYear() % 100, today.getMonth() + 1, today.getDate()].map((n) => String(n).padStart(2, '0')).join('');
  return `${date}_${centre}_${tenant}_CS REVIEW`;
}

/** A user's saved language as a review language; unset means the dossier's own language decides. */
export const reviewLanguageFromPreference = (language: 'ENGLISH' | 'FRENCH' | null | undefined): 'fr' | 'en' | undefined =>
  language === 'FRENCH' ? 'fr' : language === 'ENGLISH' ? 'en' : undefined;

/** The current round's points that already carry a verdict, in guideline order. */
export function judgedReviewPointIds(deck: SlideDeckDTO): string[] {
  const review = deck.doc.review;
  if (!review || review.round !== deck.reviewRound) return [];
  return SLIDES_VALIDATION_POINTS.filter(({ id }) => Object.hasOwn(VERDICT_MARK, review.points?.[id]?.verdict ?? '')).map(({ id }) => id);
}

export function reviewHasCompleteResult(deck: SlideDeckDTO): boolean {
  return !!deck.doc.review?.synthesis?.trim() && judgedReviewPointIds(deck).length === SLIDES_VALIDATION_POINTS.length;
}

/**
 * How far a review has got, read off what it has produced, so it survives a stop, a reload and a
 * resume. It follows the review's own passes (REVIEW_PROMPT): label every page (15%), judge p1..p10
 * (75%: each callout placed for a point not yet judged counts a fifth of it, up to four fifths),
 * write the synthesis (10%). `exact` is unrounded; `next` is where the step under way ends, which
 * the running bar eases toward between saved results.
 */
export function reviewProgress(deck: SlideDeckDTO): { percent: number; exact: number; next: number; step: string } {
  const points = SLIDES_VALIDATION_POINTS.length;
  if (reviewHasCompleteResult(deck)) return { percent: 100, exact: 100, next: 100, step: 'Review written' };
  const sheets = deck.doc.pages.filter(page => !page.cover);
  const labelled = sheets.filter(page => page.label?.trim()).length;
  const judged = new Set(judgedReviewPointIds(deck));
  const callouts = new Map<string, number>();
  for (const el of sheets.flatMap(page => page.elements)) {
    if (el.origin === 'agent' && el.guidelinePointId) callouts.set(el.guidelinePointId, (callouts.get(el.guidelinePointId) ?? 0) + 1);
  }
  const pointWork = SLIDES_VALIDATION_POINTS.reduce((sum, { id }) => sum + (judged.has(id) ? 1 : Math.min(0.8, 0.2 * (callouts.get(id) ?? 0))), 0);
  const synthesis = deck.doc.review?.round === deck.reviewRound && !!deck.doc.review.synthesis?.trim();
  const labelShare = 15 * labelled / Math.max(1, sheets.length);
  const exact = Math.min(99.9, labelShare + 75 * pointWork / points + (synthesis ? 10 : 0));
  const labelling = judged.size === 0 && labelled < sheets.length;
  const next = labelling ? labelShare + 15 / Math.max(1, sheets.length)
    : judged.size < points ? 15 + 75 * (judged.size + 1) / points : 100;
  const step = labelling ? `Labelling page ${labelled + 1} of ${sheets.length}`
    : judged.size < points ? `Point ${judged.size + 1} of ${points}` : 'Writing the summary';
  return { percent: Math.min(99, Math.floor(exact)), exact, next: Math.max(exact, Math.min(99.9, next)), step };
}

/** The directive behind Resume review: carry on a stopped review without redoing what it judged. */
export function resumeReviewPrompt(deck: SlideDeckDTO): string {
  const judged = judgedReviewPointIds(deck);
  const remaining = SLIDES_VALIDATION_POINTS.map(({ id }) => id).filter(id => !judged.includes(id));
  return 'Resume the review of this deck that was stopped before it finished. Use the project context already supplied in this conversation; start now without asking for more context. ' +
    'Keep every verdict, annotation and stamp already placed: do not clear, repeat or re-place them. ' +
    'Skip Pass 0 if the pages are already labelled. ' +
    (remaining.length
      ? `Points already judged: ${judged.join(', ') || 'none'}. Work only ${remaining.join(', ')}, in that order, placing localized annotations as you go, then close with the stamp and synthesis.`
      : 'Every point already has a verdict: close with the stamp and synthesis.');
}

export function proposalHasCompleteResult(deck: SlideDeckDTO): boolean {
  const run = deck.doc.proposalRun;
  return !!run && run.round === deck.reviewRound && run.sheets?.length === 2 &&
    new Set(run.sheets.map(sheet => sheet.pageId)).size === 2 && run.sheets.every(sheet => {
      const page = deck.doc.pages.find(item => item.id === sheet.pageId);
      return !!page && sheet.imageId !== sheet.cartoucheId &&
        page.elements.some(item => item.id === sheet.imageId && item.type === 'image' && item.url === sheet.imageUrl) &&
        page.elements.some(item => item.id === sheet.cartoucheId && item.type === 'image' && item.url === sheet.cartoucheUrl);
    });
}
